import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Mail, X } from "lucide-react";

/**
 * Global plan-quota gate modal. Listens for the `aivos:quota` window event
 * dispatched by the axios interceptor in `lib/api.js` whenever a 402 with a
 * structured `{code, kind, message, plan, limit, used}` payload comes back
 * from POST /clients or POST /projects.
 *
 * Mounted once at the App root so any page that triggers a quota-gated
 * create operation gets a clean upgrade prompt with a "Contact Admin" CTA.
 */
export default function QuotaModal() {
    const [info, setInfo] = useState(null);
    const nav = useNavigate();

    useEffect(() => {
        const onQuota = (e) => setInfo(e.detail || null);
        window.addEventListener("aivos:quota", onQuota);
        return () => window.removeEventListener("aivos:quota", onQuota);
    }, []);

    if (!info) return null;

    const isNoPlan = info.code === "no_plan";
    const planLabel = info.plan_label || (info.plan ? info.plan : "No plan");
    const limit = info.limit ?? 0;
    const kindLabel = info.kind === "clients" ? "clients" : "campaigns";

    const close = () => setInfo(null);
    const goSettings = () => { close(); nav("/settings"); };
    const mailAdmin = () => {
        // Best-effort mailto. We don't ship a global "system admin email" so
        // fall back to the configured contact email if any. Letting the user
        // decide the recipient is acceptable for an intentional upgrade.
        const subject = encodeURIComponent("Furmais — Upgrade request");
        const body = encodeURIComponent(
            isNoPlan
                ? "Hi admin, please assign me a plan so I can start using Furmais."
                : `Hi admin, I've hit my ${planLabel} plan limit (${limit} ${kindLabel}). Please consider upgrading my account.`
        );
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
    };

    return (
        <div
            className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            data-testid="quota-modal"
            onClick={close}
        >
            <div
                className="card-base w-full max-w-md p-6 sm:p-8 relative"
                onClick={e => e.stopPropagation()}
            >
                <button
                    onClick={close}
                    data-testid="quota-modal-close"
                    className="absolute top-4 right-4 text-zinc-500 hover:text-white"
                    aria-label="Close"
                >
                    <X className="w-5 h-5" />
                </button>

                <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-sm border border-[#FF4400]/40 bg-[#FF4400]/10 flex items-center justify-center shrink-0">
                        <Lock className="w-5 h-5 text-[#FF4400]" />
                    </div>
                    <div className="min-w-0">
                        <div className="label-micro text-[#FF4400]">
                            {isNoPlan ? "No plan assigned" : "Plan limit reached"}
                        </div>
                        <h3 className="font-display text-2xl mt-0.5">
                            {isNoPlan ? "You need a plan" : `${planLabel} plan limit hit`}
                        </h3>
                    </div>
                </div>

                <p className="text-sm text-zinc-300 leading-relaxed">
                    {info.message || "You have reached your plan limit. Please upgrade or contact admin."}
                </p>

                {!isNoPlan && (
                    <div
                        className="mt-4 grid grid-cols-2 gap-2 p-3 border border-zinc-900 rounded-sm text-center"
                        data-testid="quota-modal-usage"
                    >
                        <div>
                            <div className="font-display text-2xl">{info.used ?? "—"}</div>
                            <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mt-1">Used</div>
                        </div>
                        <div>
                            <div className="font-display text-2xl">{limit}</div>
                            <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mt-1">Limit</div>
                        </div>
                    </div>
                )}

                <div className="mt-6 flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        data-testid="quota-modal-contact"
                        onClick={mailAdmin}
                        className="btn-accent text-sm"
                    >
                        <Mail className="w-4 h-4" />
                        Contact admin
                    </button>
                    <button
                        type="button"
                        data-testid="quota-modal-upgrade"
                        onClick={goSettings}
                        className="btn-secondary text-sm"
                    >
                        View plan
                    </button>
                    <button
                        type="button"
                        onClick={close}
                        className="ml-auto text-xs font-mono uppercase tracking-wider text-zinc-500 hover:text-white"
                    >
                        Dismiss
                    </button>
                </div>
            </div>
        </div>
    );
}
